/* (c) 2020-2023, Nextwave Logic, Inc. All Rights Reserved */

import React, { useEffect, useContext, useState } from 'react';
import { Form, FormGroup, Input, InputGroup, InputGroupAddon, Label, Button, Pagination, PaginationItem, PaginationLink, Modal, ModalHeader, ModalBody, Col } from 'reactstrap';
import moment from 'moment';
import Alert from 'react-s-alert';
import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

// context imports
import { ThemeContext } from '../../contexts/ThemeContext'

// import services
import AdminService from '../../services/AdminService';
import GeneralService from '../../services/GeneralService';

// local imports
import ErrorBoundary from '../general/ErrorBoundary';

const EditService = props => {

    // variables
    let timeout = 0;
    let isRemove = false;

    // states
    const [service, setService] = useState({
        name: '',
        overview: '',
        image: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8UA8AAmUBcaVexNkAAAAASUVORK5CYII=',
        sections: [],
        status: 'active'
    });
    const [editor, setEditor] = useState();
    const [isDataModified, setIsDataModified] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState([]);
    const [isLoading3, setIsLoading3] = useState(false);
    const [modal, setModal] = useState({ isVisible: false, data: { from: '', to: '', code: '', amount: 0, qty: 0, payment_id: '', notes: { your_name: '', email: '', phone: '', email_of_receiver: '', name_of_receiver: '', notes_for_receiver: '' }, sentToUser: false } });
    const [pagination, setPagination] = useState({ limit: 10, page: 1 });
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // contexts
    const { theme } = useContext(ThemeContext);

    // use Effects
    useEffect(() => {
        // console.log('state', props.location)
        if (props.location.state) {
            AdminService.getService(props.location.state.id).then(data => {
                // console.log('data', data);
                setService(data.service);
                setIsLoading(false);
            });
        } else {
            window.location.pathname = 'admin/dashboard/services';
        }
        return () => {
            window.onbeforeunload = undefined
            setIsDataModified(false);
        }
    }, [])

    useEffect(() => {
        if (isDataModified) {
            window.onbeforeunload = () => true
        } else {
            window.onbeforeunload = undefined
        }
    }, [isDataModified]);

    // utility functions
    const openImageSelector = e => {
        document.getElementById("imageInput").click();
    }

    // handle functions
    const handleImageChange = e => {
        try {
            let file = document.getElementById("imageInput").files[0];
            let reader = new FileReader();

            if (!file) {
                console.log("select file")
                return;
            } else if (file.size > 5e+6) {
                Alert.info("Please select file of size less then 5mb", {
                    position: 'top-right',
                    effect: 'slide'
                });
                return;
            }

            reader.readAsDataURL(file);

            reader.onloadend = () => {
                setService({ ...service, image: reader.result });
                setIsDataModified(true);
            };
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin EditService handleImageChange', 'Error in try catch block', err.stack);
        }
    }

    const handleAddSection = () => {
        let temp = [...service.sections];
        temp.push({ title: '', description: '' });
        setService({ ...service, sections: temp });
    }

    const handleRemoveSection = (index) => {
        isRemove = true;
        let temp = [...service.sections];
        temp.splice(index, 1);
        setService({ ...service, sections: temp });
        setTimeout(() => {
            isRemove = false;
        }, [500])
    }

    const handleChange = e => {
        try {
            setService({ ...service, [e.target.name]: e.target.value });
            setIsDataModified(true);
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin EditService handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSectionChange = (index, type, value) => {
        try {
            if (!isRemove) {
                let temp = [...service.sections];
                if (type === 'title') {
                    temp[index].title = value;
                } else if (type === 'description') {
                    temp[index].description = value;
                }
                setService({ ...service, sections: temp });
                setIsDataModified(true);
            }
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            GeneralService.logError('at Admin EditService handleChange', 'Error in try catch block', err.stack);
        }
    }

    const handleSubmit = e => {
        if (e) e.preventDefault();
        try {
            AdminService.putService(service, service._id).then(data => {
                props.history.replace('services');
                // setService(data.service);
                setIsLoading(false);
            });
        } catch (err) {
            Alert.error("Some error occured, please try again later or report support team.", {
                position: 'top-right',
                effect: 'slide'
            });
            setIsLoading(false);
            GeneralService.logError('at Admin EditService handleSearchSubmit', 'Error in try catch block', err.stack);
        }
    }

    const showPreview = () => {
        props.history.push({
            pathname: `/admin/dashboard/viewService`,
            state: { service }
        });
    }

    return (
        <main>
            <div className="container-fluid">
                <div className="my-4 d-flex">
                    <h3>Edit Service</h3>
                    <h5 className='ml-auto' align="center"><a style={{ color: "#384E73" }} href="#" onClick={() => showPreview()}>View Page</a></h5>
                </div>
                <hr />
                <div className="mb-4">
                    <div>
                        <h4>Basic</h4>
                        <div className='px-5'>
                            <FormGroup>
                                <Label style={theme.formBodyText} >Service Name</Label>
                                <Input type="text" name="name" className="form-control" style={theme.formInput} value={service.name} onChange={(e) => handleChange(e)} required />
                            </FormGroup>
                            <FormGroup>
                                <Label style={theme.formBodyText} >Service Overview ( upto 300 characters )</Label>
                                <Input type="textarea" name="overview" rows="5" className="form-control" style={theme.formInput} value={service.overview} onChange={(e) => handleChange(e)} required />
                            </FormGroup>
                            <label htmlFor="imageDiv" style={theme.formBodyText} >Service Image (optional)</label>
                            <div className="d-flex" id="imageDiv">
                                <div style={{ width: "120px", height: "120px" }}>
                                    <img className="m-auto w-100 h-100" id="image" src={service.image} alt="Image" style={{ border: "solid 3px #C4C4C4" }} />
                                </div>
                                <button className="btn mt-auto ml-3"
                                    type="button"
                                    onClick={openImageSelector}
                                    style={Object.assign({ height: 'fit-content' }, theme.formButton)}>Edit</button>
                                <div style={{ visibility: 'hidden', height: '0px' }}>
                                    <input type="file" id="imageInput" accept=".jpg, .jpeg, .png" onChange={handleImageChange} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <hr />
                    <div>
                        <h4>Sections</h4>
                        <div className='px-5'>
                            {
                                service.sections.map((section, index) => (
                                    <div key={index}>
                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Section Title</Label>
                                            <Input type="text" className="form-control" style={theme.formInput} name="title" value={section.title} onChange={(e) => handleSectionChange(index, 'title', e.target.value)} required />
                                        </FormGroup>
                                        <FormGroup>
                                            <Label style={theme.formBodyText} >Section Description</Label>
                                            <CKEditor
                                                editor={ClassicEditor}
                                                data={section.description}
                                                config={{
                                                    placeholder: "You can write description with different styles",
                                                    toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList', '|', 'indent', 'outdent', '|', 'insertTable', 'undo', 'redo']
                                                }}
                                                onInit={editor => {
                                                    // setEditor(editor);
                                                    editor.editing.view.change(writer => {
                                                        writer.setStyle("height", "22rem", editor.editing.view.document.getRoot());
                                                    });
                                                }}
                                                onChange={(event, editor) => {
                                                    const data = editor.getData();
                                                    handleSectionChange(index, 'description', data);
                                                }}
                                            />
                                        </FormGroup>
                                        <div style={{ cursor: 'pointer' }} onClick={() => handleRemoveSection(index)}>- Remove Section</div>
                                        <hr />
                                    </div>
                                ))
                            }
                            <div style={{ cursor: 'pointer' }} onClick={() => handleAddSection()}>+ Add Section</div>
                        </div>
                    </div>
                </div>
                <br />
                <Button type="submit" style={isDataModified ? theme.buttonWarning : theme.button} onClick={() => handleSubmit()}>Save Changes</Button>
            </div>
        </main >
    )

}

export default EditService;